import type { MutationFilterInput, Register } from 'generated/gql';
import {
  AccountEntitlement,
  PartySearchType,
  SortOrder,
  useMutationsQuery,
  usePartyDashboardQuery,
} from 'generated/gql';
import {
  Box,
  Card,
  ContentWrapper,
  DrawerList,
  DrawerListItem,
  EmptyState,
  Flex,
  Heading,
  Link,
  Loader,
  PlusIcon,
  UploadIcon,
  useGreaterThan,
} from '@rulebooks/evidend-titan';
import { t, Trans } from '@lingui/macro';
import { Column, Columns } from 'components/Columns';
import { GreyBackground } from 'components/GreyBackground/GreyBackground';
import { IncorporationStartModal } from 'components/IncorporationStartModal/IncorporationStartModal';
import { RegisterSignatureTag } from 'components/register/RegisterSignature/RegisterSignatureTag';
import { SidebarNavigation } from 'components/SidebarNavigation/SidebarNavigation';
import { formatMutationType, formatMutationTypeLink } from 'helpers/formatMutation';
import { formatDateFromNow } from 'helpers/formatShares';
import { PageTitle } from 'helpers/PageTitle';
import { useAccount, useEntitlements, useHasLicenseAgreement } from 'hooks/useAccount';
import { orderBy } from 'lodash';
import type { NextPage } from 'next';
import React, { useCallback, useState } from 'react';
import styled from 'styled-components';
import { JsonSidebarForm } from '../components/JsonSidebarForm/JsonSidebarForm';
import { ConfirmModal } from '../components/ConfirmModal/ConfirmModal';
import { useRouter } from 'next/router';

const StyledDrawerList = styled(DrawerList)`
  margin-left: 0 !important;
  margin-right: 0 !important;
  padding-left: 0 !important;
  padding-right: 0 !important;
`;

const StyledDrawerListItem = styled(DrawerListItem)`
  margin-left: 0 !important;
  margin-right: 0 !important;
  padding-left: 0 !important;
  padding-right: 0 !important;
`;

const DashboardItem = ({ children, action, title }: any) => (
  <Card spaceAfter={8}>
    <Box py={2}>
      <Flex justifyContent="space-between" spaceAfter={4}>
        <Heading heading="h4">{title}</Heading>
        {action && <Flex alignItems="center">{action}</Flex>}
      </Flex>
      {children}
    </Box>
  </Card>
);

const RecentRegisters = () => {
  // const [, canCreateRegister] = useEntitlements([AccountEntitlement.RegisterCreate]);
  const hasLicenseAgreement = useHasLicenseAgreement();
  const [, hasCreateRegisterEntitlement] = useEntitlements([AccountEntitlement.RegisterCreate]);
  const canCreateRegister = true;
  const [, hasSuperAdmin] = useEntitlements([AccountEntitlement.SuperAdmin]);
  const router = useRouter();
  const [isIncorporationModalShown, setIncorporationModalShown] = useState<boolean>(false);
  const [isUpgradeModalShown, setUpgradeModalShown] = useState<boolean>(false);
  const [isRegisterDrawerShown, setIsRegisterDrawerShown] = useState<boolean>(false);
  const { data: { partySearch } = {} } = usePartyDashboardQuery({
    variables: {
      pagination: {
        page: 0,
        size: 6,
        sort: [{ field: 'recentlyViewed', order: SortOrder.Desc }],
      },
      filter: {
        type: PartySearchType.AccessibleRegisters,
      },
    },
  });

  const handleIncorporationModal = useCallback(
    (isShow: boolean) => {
      if (isShow && (!hasLicenseAgreement || !hasCreateRegisterEntitlement)) {
        setUpgradeModalShown(true);
      } else {
        setIncorporationModalShown(isShow);
      }
    },
    [
      hasLicenseAgreement,
      hasCreateRegisterEntitlement,
      setUpgradeModalShown,
      setIncorporationModalShown,
    ],
  );

  const onUpgradeConfirm = useCallback(() => {
    setUpgradeModalShown(false);
    router.push('/account-upgrade');
  }, [setUpgradeModalShown, router]);

  if (partySearch) {
    if (partySearch.items.length === 0) {
      return (
        <DashboardItem title={t`Recent`}>
          <Flex justifyContent="center">
            <Box py={20}>
              <EmptyState
                isCentered
                description={
                  hasCreateRegisterEntitlement ? (
                    <Trans>
                      Er zijn geen registers gevonden waar je toegang toe hebt. Registreer een
                      oprichting binnen Evidend door met onderstaande knop een register aan te
                      maken.
                    </Trans>
                  ) : (
                    <Trans>
                      Er zijn geen registers met je gedeeld. Zodra een register met je gedeeld is,
                      kun je het hier terugvinden.
                    </Trans>
                  )
                }
                title={<Trans>Maak een eerste register aan</Trans>}
              >
                {hasSuperAdmin && (
                  <Link
                    prefixAdornment={<UploadIcon />}
                    onClick={() => setIsRegisterDrawerShown(true)}
                  >
                    <Trans>Register importeren</Trans>
                  </Link>
                )}
                <Link
                  prefixAdornment={<PlusIcon />}
                  onClick={() => handleIncorporationModal(true)}
                  style={{ marginLeft: 8 }}
                >
                  <Trans>Register aanmaken</Trans>
                </Link>
              </EmptyState>
            </Box>
          </Flex>

          <IncorporationStartModal
            isModalShown={isIncorporationModalShown}
            onModalClose={() => handleIncorporationModal(false)}
          />

          <ConfirmModal
            confirmButtonLabel={t`Upgrade`}
            confirmButtonVariant="primary"
            isShown={isUpgradeModalShown}
            title={t`Upgrade your account`}
            onCancel={() => setUpgradeModalShown(false)}
            onConfirm={onUpgradeConfirm}
          >
            <Trans>
              The requested action is available for Basic and Corporate accounts only. Please
              upgrade your account to proceed.
            </Trans>
          </ConfirmModal>

          <JsonSidebarForm
            isSidebarShown={isRegisterDrawerShown}
            onSidebarClose={() => setIsRegisterDrawerShown(false)}
            onUpdate={(registerId) => {
              router.push(`/rechtspersoon/${registerId}`);
            }}
          />
        </DashboardItem>
      );
    }

    return (
      <DashboardItem
        action={[
          ...(hasSuperAdmin
            ? [
                <Link
                  key="import-register-button"
                  prefixAdornment={<UploadIcon />}
                  onClick={() => setIsRegisterDrawerShown(true)}
                >
                  <Trans>Register importeren</Trans>
                </Link>,
              ]
            : []),
          ...(canCreateRegister
            ? [
                <Link
                  key="incorporation-button"
                  prefixAdornment={<PlusIcon />}
                  onClick={() => handleIncorporationModal(true)}
                  style={{ marginLeft: 8 }}
                >
                  <Trans>Register aanmaken</Trans>
                </Link>,
              ]
            : []),
        ]}
        title={t`Recent`}
      >
        <StyledDrawerList spaceAfter={6}>
          {partySearch.items.map((result) => {
            if (!result.legalEntity) return null;
            const { id, countryOfRegistration, name, tradeRegisterNumber } = result.legalEntity;
            const isCountryNL = countryOfRegistration === 'NL';

            return (
              <StyledDrawerListItem
                key={id}
                description={
                  <Flex justifyContent="space-between">
                    {isCountryNL ? t`KVK: ${tradeRegisterNumber}` : tradeRegisterNumber || ''}
                    {result.legalEntity.register && (
                      <RegisterSignatureTag {...(result.legalEntity.register as Register)} />
                    )}
                  </Flex>
                }
                linkTo={`/rechtspersoon/${id}`}
                title={name}
              />
            );
          })}
        </StyledDrawerList>

        <Link to="/registers">
          <Trans>Bekijk al mijn registers ({partySearch.pagination.totalCount})</Trans>
        </Link>

        <IncorporationStartModal
          isModalShown={isIncorporationModalShown}
          onModalClose={() => handleIncorporationModal(false)}
        />

        <ConfirmModal
          confirmButtonLabel={t`Upgrade`}
          confirmButtonVariant="primary"
          isShown={isUpgradeModalShown}
          title={t`Upgrade your account`}
          onCancel={() => setUpgradeModalShown(false)}
          onConfirm={onUpgradeConfirm}
        >
          <Trans>
            The requested action is available for Basic and Corporate accounts only. Please upgrade
            your account to proceed.
          </Trans>
        </ConfirmModal>

        <JsonSidebarForm
          isSidebarShown={isRegisterDrawerShown}
          onSidebarClose={() => setIsRegisterDrawerShown(false)}
          onUpdate={(registerId) => {
            router.push(`/rechtspersoon/${registerId}`);
          }}
        />
      </DashboardItem>
    );
  }

  return (
    <DashboardItem title={t`Recent`}>
      <Box py={20}>
        <Flex alignItems="center" justifyContent="center">
          <Loader height={80} variant="spinner" width="80" />
        </Flex>
      </Box>
    </DashboardItem>
  );
};

const PendingTransactions = () => {
  const account = useAccount();

  const { data: { mutations } = {} } = useMutationsQuery({
    variables: {
      filter: {
        isDraft: true,
      } as MutationFilterInput,
      pagination: {
        page: 0,
        size: 10000,
        sort: [],
      },
    },
  });

  if (!mutations)
    return (
      <DashboardItem title={t`Pending transactions`}>
        <Box py={20}>
          <Flex alignItems="center" justifyContent="center">
            <Loader height={80} variant="spinner" width="80" />
          </Flex>
        </Box>
      </DashboardItem>
    );

  const totalCount = mutations.items.length;
  const filteredMutations = mutations.items.filter(
    (mutation) => mutation.lastUpdatedBy.id === account?.user.id,
  );
  const items = orderBy(filteredMutations, 'lastUpdatedOn', 'desc');

  if (items.length === 0) {
    return (
      <DashboardItem title={t`Pending transactions`}>
        <Flex justifyContent="center">
          <Box py={20}>
            <EmptyState
              isCentered
              description={t`Een workflow voor een mutatie kan gestart worden vanuit een register.`}
              title={t`Je hebt geen actieve workflows`}
            />
          </Box>
        </Flex>
      </DashboardItem>
    );
  }

  return (
    <DashboardItem title={t`Pending transactions`}>
      <StyledDrawerList spaceAfter={6}>
        {items.map((mutation) => {
          const { id, type, lastUpdatedOn, register } = mutation;
          const mutationTypeLabel = formatMutationType(mutation);
          const mutationTypeLink = formatMutationTypeLink(type);
          const titleName = register.legalEntity.name || t`Onbekend`;
          return (
            <StyledDrawerListItem
              key={id}
              description={t`Laatst bijgewerkt: ${formatDateFromNow(lastUpdatedOn)}`}
              linkTo={`${mutationTypeLink}/${id}`}
              title={`${mutationTypeLabel}: ${titleName}`}
            />
          );
        })}
      </StyledDrawerList>

      <Link to="/mutaties/overzicht">
        <Trans>Bekijk alle workflows ({totalCount})</Trans>
      </Link>
    </DashboardItem>
  );
};

export const Home: NextPage = () => {
  const isLarge = useGreaterThan('s');

  return (
    <>
      <PageTitle title={t`Home`} />
      <SidebarNavigation />

      <GreyBackground />

      <ContentWrapper maxWidth={1040} spaceAfter={8}>
        <Box pt={20}>
          <Heading heading="h2" renderAs="h1" spaceAfter={6}>
            <Trans>Home</Trans>
          </Heading>
        </Box>

        <Columns count={isLarge ? 2 : 1} gap={8}>
          <Column>
            <RecentRegisters />
          </Column>

          <Column>
            <PendingTransactions />
          </Column>
        </Columns>
      </ContentWrapper>
    </>
  );
};

export default Home;
